import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import feather from 'feather-icons';
import { Subscription, forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { HeaderService } from '../../../../../services/header/header.service';
import {
  EntrepotTableRow,
  LogicielCaisseEntrepotService,
} from '../../../../../services/logiciel-caisse/logiciel-caisse-entrepot.service';
import {
  LogicielCaisseReasortTransfertService,
  REASORT_IS,
  ReasortTransfertDetail,
} from '../../../../../services/logiciel-caisse/logiciel-caisse-reasort-transfert.service';

interface DetailLineRow {
  reference: string;
  libelle: string;
  codeBarre: string;
  couleurTaille: string;
  quantite: number;
}

@Component({
  selector: 'app-reassort-detail',
  templateUrl: './reassort-detail.component.html',
  styleUrls: ['./reassort-detail.component.scss'],
})
export class ReassortDetailComponent implements OnInit, AfterViewInit, OnDestroy {
  transfertId = 0;
  loading = true;
  busy = false;

  numDocument = '—';
  documentDate = '—';
  documentStatut = '—';
  note = '—';
  isReception: number | null = null;
  entrepotDepart = '—';
  entrepotDestination = '—';
  entrepotDestinationCode = '—';
  ligneRows: DetailLineRow[] = [];
  totalQty = 0;

  private routeSub?: Subscription;
  private loadSub?: Subscription;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly headerService: HeaderService,
    private readonly reasortService: LogicielCaisseReasortTransfertService,
    private readonly entrepotService: LogicielCaisseEntrepotService,
    private readonly toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.routeSub = this.route.queryParamMap.subscribe((qp) => {
      const id = Number(qp.get('id'));
      this.transfertId = Number.isFinite(id) && id > 0 ? id : 0;
      if (!this.transfertId) {
        this.toastr.error('Identifiant document manquant', 'Réassort');
        void this.goBack();
        return;
      }
      this.loadDetail();
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => feather.replace(), 0);
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
    this.loadSub?.unsubscribe();
  }

  canApprouver(): boolean {
    return this.isReception === REASORT_IS.CREE;
  }

  canConfirmerReception(): boolean {
    return this.isReception === REASORT_IS.EN_COURS_LIVRAISON;
  }

  /** Archive uniquement si non encore approuvé (statut Créé). */
  canArchiver(): boolean {
    return this.isReception === REASORT_IS.CREE;
  }

  goBack(): void {
    const tab =
      this.isReception === REASORT_IS.RECEPTION_TERMINEE ||
      this.isReception === REASORT_IS.ANNULE
        ? 'clos'
        : 'en-cours';
    void this.router.navigate(['/achat/reassort', tab]);
  }

  approuver(): void {
    if (!this.canApprouver() || this.busy) {
      return;
    }
    this.busy = true;
    this.applyHeader();
    this.reasortService.approuverTransfert(this.transfertId).subscribe({
      next: () => {
        this.busy = false;
        this.toastr.success('Document approuvé — en attente de confirmation de réception.', 'Réassort');
        this.reasortService.notifyListRefresh();
        this.loadDetail();
      },
      error: (err: Error) => {
        this.busy = false;
        this.applyHeader();
        this.toastr.error(err?.message || 'Échec de l’approbation', 'Erreur');
      },
    });
  }

  archiver(): void {
    if (!this.canArchiver() || this.busy) {
      return;
    }
    const ok = window.confirm(
      'Archiver ce réassort ? Il ne pourra plus être approuvé (document non approuvé uniquement).'
    );
    if (!ok) {
      return;
    }
    this.busy = true;
    this.applyHeader();
    this.reasortService.archiverTransfert(this.transfertId).subscribe({
      next: () => {
        this.busy = false;
        this.toastr.success('Réassort archivé.', 'Réassort');
        this.reasortService.notifyListRefresh();
        this.isReception = REASORT_IS.ANNULE;
        this.goBack();
      },
      error: (err: Error) => {
        this.busy = false;
        this.applyHeader();
        this.toastr.error(err?.message || 'Échec de l’archivage', 'Erreur');
      },
    });
  }

  confirmerReception(): void {
    if (!this.canConfirmerReception() || this.busy) {
      return;
    }
    this.busy = true;
    this.applyHeader();
    this.reasortService.confirmerReception(this.transfertId).subscribe({
      next: () => {
        this.busy = false;
        this.toastr.success('Réception confirmée — stock destination alimenté.', 'Réassort');
        this.reasortService.notifyListRefresh();
        this.loadDetail();
      },
      error: (err: Error) => {
        this.busy = false;
        this.applyHeader();
        this.toastr.error(err?.message || 'Échec de la confirmation', 'Erreur');
      },
    });
  }

  private loadDetail(): void {
    this.loading = true;
    this.loadSub?.unsubscribe();
    this.loadSub = forkJoin({
      detail: this.reasortService.getById(this.transfertId),
      entrepots: this.entrepotService.getEntrepotsErpTableRows().pipe(catchError(() => of([] as EntrepotTableRow[]))),
    }).subscribe({
      next: ({ detail, entrepots }) => {
        this.hydrate(detail, entrepots);
        this.loading = false;
        this.applyHeader();
        setTimeout(() => feather.replace(), 0);
      },
      error: (err: Error) => {
        this.loading = false;
        this.toastr.error(err?.message || 'Impossible de charger le document', 'Erreur');
        this.applyHeader();
      },
    });
  }

  private hydrate(detail: ReasortTransfertDetail, entrepots: EntrepotTableRow[]): void {
    const byId = new Map(entrepots.map((e) => [Number(e.id), e]));
    const depart = byId.get(Number(detail.entrepot_id_depart));
    const dest = byId.get(Number(detail.entrepot_id_reception));

    this.numDocument = detail.num_document || '—';
    this.documentDate = this.formatDate(detail.date_transfert);
    this.isReception = Number(detail.is_reception);
    this.documentStatut = this.statutLabel(this.isReception);
    this.note = detail.note?.trim() ? detail.note.trim() : '—';
    this.entrepotDepart = depart?.LIBELLÉ || `Entrepôt #${detail.entrepot_id_depart}`;
    this.entrepotDestination = dest?.LIBELLÉ || `Entrepôt #${detail.entrepot_id_reception}`;
    this.entrepotDestinationCode = dest?.['CODE ENTREPÔT'] || '—';

    const lines: DetailLineRow[] = [];
    let qty = 0;
    for (const lg of detail.lignes || []) {
      const barcodes = Array.isArray(lg.barcodes) ? lg.barcodes : [];
      if (!barcodes.length) {
        lines.push({
          reference: lg.reference || '—',
          libelle: lg.libelle_article || 'Article',
          codeBarre: '—',
          couleurTaille: '',
          quantite: 0,
        });
        continue;
      }
      for (const bc of barcodes) {
        const q = Math.max(0, Math.round(Number(bc.quantite) || 0));
        qty += q;
        const ct = [bc.couleur, bc.taille].filter(Boolean).join(' / ');
        lines.push({
          reference: lg.reference || '—',
          libelle: lg.libelle_article || 'Article',
          codeBarre: bc.code_barre || '—',
          couleurTaille: ct,
          quantite: q,
        });
      }
    }
    this.ligneRows = lines;
    this.totalQty = Math.max(0, Math.round(qty || Number(detail.quantite) || 0));
  }

  private statutLabel(is: number | null): string {
    switch (is) {
      case REASORT_IS.RECEPTION_TERMINEE:
        return 'Reçu';
      case REASORT_IS.CREE:
        return 'Créé';
      case REASORT_IS.ANNULE:
        return 'Archivé';
      case REASORT_IS.EN_COURS_LIVRAISON:
        return 'En cours de livraison';
      default:
        return '—';
    }
  }

  private formatDate(raw: string): string {
    if (!raw) {
      return '—';
    }
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) {
      return String(raw);
    }
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd} / ${mm} / ${yyyy}`;
  }

  private applyHeader(): void {
    type BtnType = 'primary' | 'secondary';
    const buttons: Array<{ label: string; action: () => void; type: BtnType }> = [
      { label: 'Retour', action: () => this.goBack(), type: 'primary' },
    ];
    if (this.canApprouver()) {
      buttons.push({
        label: this.busy ? 'Approbation…' : 'Approuver',
        action: () => this.approuver(),
        type: 'primary',
      });
    }
    if (this.canArchiver()) {
      buttons.push({
        label: this.busy ? 'Archivage…' : 'Archiver',
        action: () => this.archiver(),
        type: 'secondary',
      });
    }
    if (this.canConfirmerReception()) {
      buttons.push({
        label: this.busy ? 'Confirmation…' : 'Confirmer la réception',
        action: () => this.confirmerReception(),
        type: 'primary',
      });
    }
    this.headerService.setConfig({
      title: 'Réassort',
      buttons,
    });
  }
}
